import { ReactElement, useState } from 'react';
import moment from 'moment';
import html2canvas from 'html2canvas';
import 'moment-timezone';
import { toast } from 'react-toastify';
import InfoCard from '../InfoCard';
import { IconSourceDescription, VIEW_BUTTONS } from '../../constants/global-constants';
import {
  ParametersType,
  TimeSpan,
  TimeSpanDateAttributes
} from '../../constants/telosair-interfaces';
import FilterDeviceAndParameters from '../../controls/FilterDeviceAndParameters';
import RefreshButton from '../RefreshButton';
import MultiTab from '../../controls/MultiTab';
import DashboardSyncCountDown from '../DashboardSyncCountdown';
import SearchInput from '../../controls/SearchInput';
import Filter from '../../controls/Filter';
import FilterChip from '../filterChip';
import { getParameterByName } from '../../_helpers/globalHelper';
import { useData } from '../../context/data.context';
import DateRangePicker from '../../controls/DateRangePicker';
import usePlan from '../../hooks/usePlan';
import { Filters, setAppliedFilter } from '../../services/localstorage.service';
import { downloadFile } from '../../_helpers/dashboardHelper';

type TimeRangeProps = {
  label: string;
  selected?: boolean;
  onClick?: () => void;
};

type DataActionHeaderProps = {
  selectedParameters: ParametersType[];
  selectedDevices: { label: string; value: number }[];
  onSelectParameters: (parameters: ParametersType[]) => void;
  onSelectDevices: (devices: { label: string; value: number }[]) => void;
  timeSpan: string;
  handleTimeChange: (span: string | TimeSpan) => void;
  selectedViews: string[];
  onSelectView: (view: string) => void;
  hasViewChangeButton?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  lastSynced?: Date;
  searchBox?: boolean;
  timeSpanDate: TimeSpanDateAttributes | null;
  setTimeSpanDate: (value: TimeSpanDateAttributes) => void;
  maxParameterCount: number;
  currentView: string;
};

/**
 * Handles create alert rule.
 * @param props - Properties of Timerange components.
 * @returns - React component for timerange.
 */
function TimeRange(props: TimeRangeProps): ReactElement {
  const { label, selected, onClick } = props;
  const SElECTEDSTYLES = 'bg-gray-500 dark:bg-white text-white dark:text-gray-400';
  return (
    <button
      onClick={onClick}
      className={` text-sm font-medium px-3 h-9 text-gray-400 hover:bg-gray-500 hover:dark:bg-white hover:text-white hover:dark:text-gray-400 rounded ${
        selected && SElECTEDSTYLES
      }`}
      type="button"
    >
      {label}
    </button>
  );
}
/** Minimum days of history each TimeSpan option requires. */
const TIMESPAN_REQUIRED_DAYS: Partial<Record<TimeSpan, number>> = {
  [TimeSpan.Week1]: 7,
  [TimeSpan.Month1]: 30
};

/**
 * DataActionHeader component.
 * @param props - Properties of DataActionHeader component.
 * @returns - ReactComponent for DataActionHeader.
 */
export default function DataActionHeader(props: DataActionHeaderProps): ReactElement {
  const {
    selectedParameters,
    selectedDevices,
    timeSpan,
    handleTimeChange,
    onSelectParameters,
    onSelectDevices,
    hasViewChangeButton,
    onRefresh,
    refreshing,
    selectedViews,
    onSelectView,
    setTimeSpanDate,
    lastSynced,
    searchBox,
    timeSpanDate,
    maxParameterCount,
    currentView
  } = props;
  const [searchText, setSearchText] = useState('');
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const { parameters, userConfigData } = useData();
  const { maxDataDays, dataDaysPlan } = usePlan();

  const minDateForPlan: Date | null =
    maxDataDays === null
      ? null
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() - (maxDataDays as number));
          d.setHours(0, 0, 0, 0);
          return d;
        })();

  const allowedTimeSpans = Object.values(TimeSpan).slice(1).filter((span) => {
    if (maxDataDays === null) return true; // unlimited
    const required = TIMESPAN_REQUIRED_DAYS[span];
    if (required === undefined) return true; // no day requirement (e.g. 15min, 1hr, custom)
    return maxDataDays >= required;
  });

  /**
   * A function to select date range.
   * @param value - Selected start and end time.
   */
  function handleApply(value: TimeSpanDateAttributes): void {
    if (value.startDate) {
      setTimeSpanDate(value);
      setAppliedFilter({
        key: Filters.dataTimeSpanCustom,
        data: value
      });
    }
    handleTimeChange(TimeSpan.Custom);
    setCustomDatePicker(false);
  }

  /**
   * A function to remove date range, updates the state setStartDate and setEndDate to null.
   */
  function handleCancel(): void {
    setCustomDatePicker(false);
    handleTimeChange(timeSpan);
  }

  /**
   * Handles on time span change.
   * @param value - Timespan value.
   */
  const onTimeChange = (value: TimeSpan): void => {
    if (value === TimeSpan.Custom) {
      setCustomDatePicker(true);
    } else {
      handleTimeChange(value);
    }
  };

  /**
   * Handles screen capture and image download.
   */
  const handleScreenCapture = async (): Promise<void> => {
    const canvas = await html2canvas(document.body);
    const dataURL = canvas.toDataURL('image/png');
    downloadFile(dataURL, `Data_${timeSpanDate?.startDate}_image.png`, 'image/png');
    toast.success('Image downloaded successfully');
  };

  /**
   * Function to handle button clicks for download, upload and capture.
   * @param id - ID of the function.
   */
  const handleClickButton = async (id: string): Promise<void> => {
    if (id === 'capture') {
      await handleScreenCapture();
    }
  };

  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div className=" rounded bg-white dark:bg-container-500 p-4 mt-4">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex items-center gap-4">
          {searchBox ? (
            <div className="hidden items-center space-x-4">
              <SearchInput textValue={searchText} setText={setSearchText} />
              <Filter position="left" />
            </div>
          ) : (
            <div className="flex items-center">
              <FilterDeviceAndParameters
                selectedDevices={selectedDevices}
                selectedParameters={selectedParameters}
                onSelectDevices={onSelectDevices}
                onSelectParameters={onSelectParameters}
                label="Select Parameters & Devices"
                position="left"
                maxParameterCount={maxParameterCount}
              />
              {!searchBox && (selectedDevices.length > 0 || selectedParameters.length > 0) && (
                <div className="flex gap-2 flex-wrap ml-2">
                  {selectedDevices.map((item) => (
                    <FilterChip
                      chip={item.label}
                      key={item.value}
                      onClick={() =>
                        onSelectDevices(selectedDevices.filter((selected) => selected !== item))
                      }
                    />
                  ))}
                  {selectedParameters.map((item) => (
                    <FilterChip
                      chip={getParameterByName(item, parameters)?.shortName || ''}
                      key={item}
                      onClick={() =>
                        onSelectParameters(
                          selectedParameters.filter((selected) => selected !== item)
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {hasViewChangeButton && (
            <MultiTab
              buttons={VIEW_BUTTONS}
              size="md"
              activeButtons={selectedViews}
              handleClick={onSelectView}
              hideButtonTextIfSm
            />
          )}
          {onRefresh && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
          {lastSynced && <DashboardSyncCountDown lastSynced={lastSynced} />}
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex flex-col gap-1">
            <div className="flex gap-2 items-center border border-gray-600 rounded">
              <div className="text-gray-400 text-sm font-medium ml-3">Time Range</div>
              <div className="flex items-center overflow-hidden rounded">
                {allowedTimeSpans.map((item) => (
                  <TimeRange
                    onClick={() => onTimeChange(item)}
                    selected={timeSpan === item}
                    key={item}
                    label={
                      // eslint-disable-next-line no-nested-ternary
                      item === TimeSpan.Custom && timeSpan === TimeSpan.Custom
                        ? `${moment(timeSpanDate?.startDate)
                            .tz(timeZone)
                            .format('Do MMM')} - ${moment(timeSpanDate?.endDate)
                            .tz(timeZone)
                            .format('Do MMM')}`
                        : item === TimeSpan.Custom
                        ? 'Custom'
                        : item.replace(/Last /g, '')
                    }
                  />
                ))}
              </div>
            </div>
            {maxDataDays !== null && maxDataDays > 0 && (
              <div className="text-xs text-gray-500 text-right">
                {dataDaysPlan} plan: up to{' '}
                {maxDataDays >= 30
                  ? `${maxDataDays / 30} month${maxDataDays / 30 !== 1 ? 's' : ''}`
                  : `${maxDataDays} day${maxDataDays !== 1 ? 's' : ''}`}{' '}
                of data
              </div>
            )}
          </div>
          <div className="pr-3 h-10 border-l border-l-gray-900" />
          <div className="flex items-center gap-4">
            {IconSourceDescription.filter((item) => item.visibleIn.includes(currentView)).map(
              (curr) => (
                <button
                  onClick={() => handleClickButton(curr.id)}
                  key={curr.id}
                  type="button"
                  className="cursor-pointer group flex"
                >
                  <span className="material-icons-outlined text-gray-400">{curr.icon}</span>
                  <div className={`${curr.class} group-hover:scale-100 max-w-[280px]`}>
                    <InfoCard title={curr.title} description={curr.description} />
                  </div>
                </button>
              )
            )}
          </div>
        </div>
      </div>
      {customDatePicker && timeSpanDate && (
        <div className="absolute right-3 mt-5 z-20">
          <DateRangePicker
            handleCancel={() => handleCancel()}
            onApply={(value) => handleApply(value)}
            timeSpanDate={timeSpanDate}
            minDate={minDateForPlan}
          />
        </div>
      )}
    </div>
  );
}
